import type { Home, ResidentBody, ResidentItem } from '@lmc/ts-common'
import { Button, EmptyState, Form, PageHeader } from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { getNominative, showToast } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useFeatureFlag from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import type { UpdateResidentReturnType } from 'hooks/useResident'
import useResident from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import UpdateAddressModal from 'screens/Rostering/ChangeManagement/UpdateAddressModal'
import {
  ButtonsWrapper,
  ProfileContent,
  ProfileWrapper
} from '../../styledComponents'
import { handleOnFinishFailed, scrollToTop } from '../../utils/utils'
import { AddressFormCard } from './FormCards/Address/AddressFormCard'
import { EmailFormCard } from './FormCards/Email'
import { PhonesFormCard } from './FormCards/Phone'
import { PreferredContactFormCard } from './FormCards/PreferredContact'
import { sanitizeAddressesForResidentUpdate } from './sanitizeAddressesForResidentUpdate'

const ResidentContactDetails = ({ testId }: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement | null>(null)
  const { id } = useParams()
  const [isDirty, setIsDirty] = useState(false)
  const [isAddressesChanged, setIsAddressesChanged] = useState(false)
  const [showUpdateAddressModal, setShowUpdateAddressModal] = useState(false)
  const { hasRota } = useHomeType()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const preferredPeopleNominative = home?.preferredPeopleNominative
  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const { resident, isLoading, updateResident } = useResident({
    id,
    onUpdateSuccess: (result?: UpdateResidentReturnType) => {
      const updatedResident = (result as { result?: ResidentItem } | undefined)
        ?.result
      const addresses = updatedResident?.addresses
      const hasInvalidGeofencingAddresses =
        Array.isArray(addresses) &&
        addresses.some(
          (addr) =>
            addr?.notValidForGeofencing === true ||
            addr?.lat == null ||
            addr?.lng == null
        )
      if (isGeofencingEnabled && hasInvalidGeofencingAddresses) {
        showToast({
          type: 'warning',
          title: t(supConstants.CHANGES_SAVED_INVALID_GEOFENCING_ADDRESSES)
        })
      } else {
        showToast({
          title: t(supConstants.RESIDENT_UPDATED, {
            variable: residentNominative
          })
        })
      }
      navigate(`../profile`, { relative: 'path' })
    }
  })
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)

  const flags = useFeatureFlag({
    flagNames: ['rostering_change_management_part_2', 'geofencing']
  })?.data

  const flagsRosteringChangeManagementPart2Enabled =
    flags?.rostering_change_management_part_2?.enabled
  const geofencingEnabled = flags?.geofencing?.enabled
  const isGeofencingEnabled =
    hasRota &&
    (home?.rotaSettings as { geofencing?: boolean } | undefined)?.geofencing ===
      true &&
    geofencingEnabled === true

  const fullName = `${resident?.name?.first} ${resident?.name?.last}`

  const handleOnFinish = async (values: Partial<ResidentBody>) => {
    if (
      hasRota &&
      flagsRosteringChangeManagementPart2Enabled &&
      isAddressesChanged &&
      !showUpdateAddressModal
    ) {
      setShowUpdateAddressModal(true)
      return
    }

    try {
      setIsUpdateLoading(true)
      setIsDirty(false)
      await updateResident({
        ...values,
        addresses: sanitizeAddressesForResidentUpdate(values.addresses)
      })
      setIsUpdateLoading(false)
    } catch {
      setIsDirty(true)
      setIsUpdateLoading(false)
    }

    if (
      hasRota &&
      flagsRosteringChangeManagementPart2Enabled &&
      isAddressesChanged
    ) {
      setShowUpdateAddressModal(false)
    }
  }

  const handleOnCancel = () => {
    navigate(`../profile`, { relative: 'path' })
  }

  const handleFormValuesChange = (values: ResidentBody) => {
    if (values) {
      setIsDirty(true)

      if (values.addresses) setIsAddressesChanged(true)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  const hasRolePermission = useAbilityAccess([
    {
      i: 'update',
      this: 'Resident',
      field: 'status'
    }
  ])

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId
            ? `${testId}-contact-details-loading`
            : 'contact-details-loading'
        }
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId
            ? `${testId}-contact-details-no-resident`
            : 'contact-details-no-resident'
        }
      />
    )
  }

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={
        testId ? `${testId}-contact-details-wrapper` : 'contact-details-wrapper'
      }
    >
      <PageHeader
        sticky
        title={t(supConstants.CONTACT_DETAILS)}
        data-testid={
          testId ? `${testId}-contact-details-header` : 'contact-details-header'
        }
      >
        <ButtonsWrapper
          data-testid={
            testId
              ? `${testId}-contact-details-buttons`
              : 'contact-details-buttons'
          }
        >
          <Button
            data-cy="cancel-btn"
            onClick={handleOnCancel}
            data-testid={
              testId
                ? `${testId}-contact-details-cancel-btn`
                : 'contact-details-cancel-btn'
            }
          >
            {hasRolePermission ? t(supConstants.CANCEL) : t(supConstants.BACK)}
          </Button>
          <Button
            data-cy="submit-btn"
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isUpdateLoading}
            disabledByPermission={!hasRolePermission}
            data-testid={
              testId
                ? `${testId}-contact-details-save-btn`
                : 'contact-details-save-btn'
            }
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent
        data-testid={
          testId
            ? `${testId}-contact-details-content`
            : 'contact-details-content'
        }
      >
        <NavigationModal canNavigate={!isDirty} />
        <Form
          form={form}
          disabled={!hasRolePermission}
          onFinish={handleOnFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={resident}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          data-testid={
            testId ? `${testId}-contact-details-form` : 'contact-details-form'
          }
        >
          <AddressFormCard residentId={resident.id} />
          <PhonesFormCard value={resident.phones || []} />
          <EmailFormCard />
          <PreferredContactFormCard form={form} />
        </Form>
      </ProfileContent>
      {showUpdateAddressModal && (
        <UpdateAddressModal
          showModal={showUpdateAddressModal}
          onCancel={() => {
            setShowUpdateAddressModal(false)
          }}
          onConfirm={() => form.submit()}
          name={fullName}
          data-testid={
            testId
              ? `${testId}-contact-details-update-address-modal`
              : 'contact-details-update-address-modal'
          }
        />
      )}
    </ProfileWrapper>
  )
}

export default ResidentContactDetails
